// Issue #172 findings 3 + 4 — one way to open the Guide.
//
// There were two entry points and both were broken, in different ways:
//
//   • Present slide CTA (`Storyboard.jsx:829`) did
//     `window.location.assign(".../analyses/:id#guide")` — a full page
//     reload to a hash nothing reads. `AnalysisDetail` mounts the drawer
//     uncontrolled and has no hash handling, so the user landed on the
//     analysis with the Guide shut.
//   • Command palette (`CommandPalette.jsx:30-37`) queried
//     `[data-testid="guide-open"]` and clicked it. That element only
//     exists while the drawer is CLOSED, so the entry was a no-op when it
//     was already open, and off an analysis page it silently did
//     `window.location.assign("/analyses")` — navigating the user away
//     with no explanation. It also advertised a `⌘J` shortcut that has no
//     handler anywhere in the app.
//
// A DOM query is the wrong contract: it couples callers to a test id and
// to the drawer's collapsed/expanded state. This is a tiny event bus
// instead — callers say "open the Guide", the drawer listens. No DOM
// knowledge either side, and it works whether the drawer is open, closed,
// or on a page that has none (where it simply does nothing).

export const GUIDE_OPEN_EVENT = "tomauro:guide:open";

/**
 * Query param for opening the Guide ACROSS a navigation.
 *
 * The event above only reaches a drawer that is already mounted. The
 * Present deck is a different route (`/analyses/:id/present`), so its CTA
 * has to navigate first — and the request has to survive the page load.
 * A query param does that; the `#guide` hash the CTA used before did not,
 * because nothing ever read it.
 */
export const GUIDE_OPEN_PARAM = "guide";

/** Should the drawer open itself on mount? */
export function shouldOpenFromUrl(search) {
    const qs = search ?? (typeof window !== "undefined" ? window.location.search : "");
    try {
        return new URLSearchParams(qs).get(GUIDE_OPEN_PARAM) === "1";
    } catch {
        return false;
    }
}

/** URL for "open this analysis with the Guide showing". */
export function analysisUrlWithGuide(analysisId, token) {
    const t = token ? `token=${encodeURIComponent(token)}&` : "";
    return `/analyses/${encodeURIComponent(analysisId)}?${t}${GUIDE_OPEN_PARAM}=1`;
}

/**
 * Drop `?guide=1` from the address bar, keeping every other param.
 *
 * PR #191 review — the param is a one-shot instruction, but it stayed in
 * the URL after the drawer opened. Three consequences: a reload or a
 * back-navigation reopened the drawer the user had just closed, the
 * param rode along into any link the user copied from the address bar,
 * and `?guide=1` showed up in shared URLs as if it were meaningful
 * state. `replaceState` leaves no extra history entry, so Back still
 * goes where the user expects.
 */
export function clearGuideParamFromUrl() {
    if (typeof window === "undefined" || !window.history?.replaceState) return false;
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(GUIDE_OPEN_PARAM)) return false;
        url.searchParams.delete(GUIDE_OPEN_PARAM);
        const qs = url.searchParams.toString();
        window.history.replaceState(
            window.history.state, "",
            `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`,
        );
        return true;
    } catch {
        return false;
    }
}

/** Ask the Guide drawer to open. Safe to call anywhere. */
export function requestGuideOpen() {
    if (typeof window === "undefined") return false;
    window.dispatchEvent(new CustomEvent(GUIDE_OPEN_EVENT));
    return true;
}

/** Subscribe. Returns an unsubscribe function. */
export function onGuideOpenRequest(handler) {
    if (typeof window === "undefined") return () => {};
    window.addEventListener(GUIDE_OPEN_EVENT, handler);
    return () => window.removeEventListener(GUIDE_OPEN_EVENT, handler);
}

/**
 * Is there an analysis in view — i.e. could a Guide exist here?
 *
 * `/analyses/new` is the wizard, which has no analysis and no drawer, so
 * offering the command there would be the same dead end the old palette
 * entry had.
 */
export function isAnalysisRoute(pathname) {
    const p = pathname || (typeof window !== "undefined" ? window.location.pathname : "");
    return /^\/analyses\/(?!new(?:\/|$))[^/]+/.test(p);
}
